/**
 * VideoEditor Component
 * 
 * Main video editor component with NLE-style interface.
 * Inspired by professional editors like Premiere Pro, DaVinci Resolve.
 * 
 * Layout:
 * ┌─────────────────────────────────────────────────────────────┐
 * │  TOOLBAR                                                    │
 * ├─────────────────────────┬───────────────────────────────────┤
 * │  SOURCE PANEL           │  PREVIEW MONITOR                  │
 * │  (Event clips list)     │  (Video preview + controls)       │
 * ├─────────────────────────┴───────────────────────────────────┤
 * │  TIMELINE                                                   │
 * │  (Clips track with drag/drop, trim handles)                 │
 * └─────────────────────────────────────────────────────────────┘
 */

import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { VideoEditorProps } from './types';
import { useEditorState } from './hooks/useEditorState';
import { getClipAtPosition } from './utils/clipUtils';
import Toolbar from './components/Toolbar';
import TabbedLeftPanel from './components/TabbedLeftPanel';
import PreviewPlayer from './components/PreviewPlayer';
import Timeline from './components/Timeline';
import SegmentEffectsInspector from './components/SegmentEffectsInspector';
import VideoSettingsInspector from './components/VideoSettingsInspector';

const VideoEditor: React.FC<VideoEditorProps> = ({
  matchId,
  sources,
  onExport,
  onClipsChange,
  toolbarRightContent,
  presets,
  initialClips = [],
  className = '',
  rangeBefore = 5,
  rangeAfter = 5,
  overlayConfig,
  onOverlayConfigChange,
  segmentEffectsConfig,
  onSegmentEffectsConfigChange,
  videoUrl,
  hasStitchedVideo = false,
  playbackMode = 'segments',
  onPlaybackModeChange,
}) => {
  const {
    state,
    addClip,
    addClips,
    removeClip,
    reorderClips,
    trimClip,
    splitClip,
    selectClip,
    setPlayhead,
    setPlaying,
    setZoom,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useEditorState(initialClips);

  // Inspector height - Dynamic based on content
  const inspectorRef = useRef<HTMLDivElement>(null);
  const [inspectorHeight, setInspectorHeight] = useState(300);

  // Get selected clip for inspector
  const selectedClip = state.selectedClipId 
    ? state.clips.find(c => c.id === state.selectedClipId) || null
    : null;

  // Measure inspector height dynamically
  // Only update when inspector content actually changes, not on every playhead update
  useEffect(() => {
    const updateInspectorHeight = () => {
      if (inspectorRef.current) {
        const height = inspectorRef.current.scrollHeight;
        setInspectorHeight((prevHeight) => {
          // Only update if height actually changed to prevent unnecessary re-renders
          if (Math.abs(prevHeight - height) > 1) {
            return height;
          }
          return prevHeight;
        });
      }
    };

    // Initial measurement with a small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      updateInspectorHeight();
    }, 0);

    // Use ResizeObserver to watch for content changes
    // Throttle ResizeObserver callbacks to prevent excessive updates during playback
    let rafId: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          updateInspectorHeight();
          rafId = null;
        });
      }
    });

    if (inspectorRef.current) {
      resizeObserver.observe(inspectorRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [state.selectedClipId, state.clips, overlayConfig, segmentEffectsConfig]);

  React.useEffect(() => {
    if (onClipsChange) {
      onClipsChange(state.clips);
    }
  }, [state.clips, onClipsChange]);

  // Handle split at current playhead position
  const handleSplit = useCallback(() => {
    if (!state.selectedClipId) return;

    // Find where playhead is relative to selected clip
    const clipInfo = getClipAtPosition(state.clips, state.playheadPosition);
    if (!clipInfo || clipInfo.clip.id !== state.selectedClipId) return;

    // Calculate split time relative to clip start
    let accumulatedTime = 0;
    for (const clip of state.clips) {
      if (clip.id === state.selectedClipId) {
        const splitTimeInClip = state.playheadPosition - accumulatedTime;
        splitClip(state.selectedClipId, splitTimeInClip);
        break;
      }
      accumulatedTime += clip.endTime - clip.startTime;
    }
  }, [state.selectedClipId, state.playheadPosition, state.clips, splitClip]);

  // Handle delete selected clip
  const handleDelete = useCallback(() => {
    if (state.selectedClipId) {
      removeClip(state.selectedClipId);
    }
  }, [state.selectedClipId, removeClip]);


  // Check if we have any storage videos in sources
  const hasStorageVideos = useMemo(() => {
    const hasEventClips = (sources.eventClips?.length || 0) > 0;
    const hasFullVideos = (sources.fullVideos?.length || 0) > 0;
    return hasEventClips || hasFullVideos;
  }, [sources]);

  if (!hasStorageVideos) {
    return (
      <div className={`flex items-center justify-center h-96 bg-white rounded-lg border border-gray-200 ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-gray-600 text-sm">No storage videos available for editing</p>
          <p className="text-gray-400 text-xs mt-1">YouTube videos cannot be edited</p>
        </div>
      </div>
    );
  }

  // Fixed heights
  const timelineHeight = 180;
  const toolbarHeight = 48;
  
  // Minimum height for main content area (left panel + video preview)
  // This ensures adequate space for source segments list and video playback
  const minMainContentHeight = 550;
  
  // Calculate bottom offset for main content (video playback area)
  // Use actual inspector height but ensure main content maintains minimum height
  const bottomComponentsHeight = timelineHeight + inspectorHeight;

  // Calculate minimum container height to accommodate all components
  const minContainerHeight = toolbarHeight + minMainContentHeight + timelineHeight + Math.max(inspectorHeight, 200);

  return (
    <div className={`relative bg-white rounded-lg shadow-md border border-gray-200 ${className}`} style={{ height: '100%', minHeight: `${minContainerHeight}px` }}>
      {/* Toolbar - Fixed at top */}
      <div className="absolute top-0 left-0 right-0 z-10" style={{ height: `${toolbarHeight}px` }}>
        <Toolbar
          canUndo={canUndo}
          canRedo={canRedo}
          zoomLevel={state.zoomLevel}
          onUndo={undo}
          onRedo={redo}
          onZoomChange={setZoom}
          clipCount={state.clips.length}
          rightContent={toolbarRightContent}
        />
      </div>

      {/* Main Content Area - Increased height for better visibility */}
      <div 
        className="absolute left-0 right-0 flex overflow-hidden"
        style={{ 
          top: `${toolbarHeight}px`, 
          bottom: `${bottomComponentsHeight}px`,
          minHeight: `${minMainContentHeight}px`
        }}
      >
        {/* Tabbed Left Panel */}
        <div className="w-72 border-r border-gray-200 flex-shrink-0 overflow-hidden h-full">
          <TabbedLeftPanel
            sources={sources}
            onAddClip={addClip}
            onAddClips={addClips}
            rangeBefore={rangeBefore}
            rangeAfter={rangeAfter}
            overlayConfig={overlayConfig}
            onOverlayConfigChange={onOverlayConfigChange}
            matchId={matchId}
          />
        </div>

        {/* Preview Monitor (Right) */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden h-full">
          <PreviewPlayer
            clips={state.clips}
            playheadPosition={state.playheadPosition}
            isPlaying={state.isPlaying}
            onPlayheadChange={setPlayhead}
            onPlayingChange={setPlaying}
            videoUrl={videoUrl}
            hasStitchedVideo={hasStitchedVideo}
            playbackMode={playbackMode}
            onPlaybackModeChange={onPlaybackModeChange}
            overlayConfig={overlayConfig}
            matchId={matchId}
          />
        </div>
      </div>

      {/* Timeline (Always visible, above inspector) */}
      <div 
        className="absolute left-0 right-0 border-t border-gray-200 bg-gray-50 overflow-visible"
        style={{ bottom: `${inspectorHeight}px`, height: `${timelineHeight}px` }}
      >
        <Timeline
          clips={state.clips}
          selectedClipId={state.selectedClipId}
          playheadPosition={state.playheadPosition}
          zoomLevel={state.zoomLevel}
          totalDuration={state.totalDuration}
          onClipSelect={selectClip}
          onClipReorder={reorderClips}
          onClipTrim={trimClip}
          onPlayheadChange={setPlayhead}
          onSplit={handleSplit}
          onDelete={handleDelete}
        />
      </div>

      {/* Inspector Panel (Always expanded, below timeline, auto-height) */}
      <div 
        ref={inspectorRef}
        className="absolute left-0 right-0 bottom-0 border-t border-gray-200 bg-white z-10"
      >
          {selectedClip ? (
            <SegmentEffectsInspector
              selectedClip={selectedClip}
              onDelete={handleDelete}
              segmentEffectsConfig={segmentEffectsConfig}
              onSegmentEffectsChange={onSegmentEffectsConfigChange}
            />
          ) : (
            overlayConfig && onOverlayConfigChange && matchId && (
              <VideoSettingsInspector
                overlayConfig={overlayConfig}
                onOverlayConfigChange={onOverlayConfigChange}
                matchId={matchId}
              />
            )
          )}
      </div>
    </div>
  );
};

export default VideoEditor;

